import { Alert, Box, Button, Stack, Typography } from "@mui/material"
import CloudDownloadIcon from "@mui/icons-material/CloudDownload"
import UploadFileIcon from "@mui/icons-material/UploadFile"
import { FC, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Sl1ImportJobDto } from "src/features/things/sl1ImportApi"
import { eyebrowSx, panelSx } from "./Sl1ImportChrome"
import { Sl1ReportSource } from "./useSl1ReportSource"

/**
 * How an operator gets a report body onto the detail screen (LOCKER-382 · D).
 *
 * TWO ways in, and the run decides which is offered:
 *
 *  - **Load from S3** when the run was published. The report head comes back in
 *    one fetch; `findings` are paged in a part at a time.
 *  - **Open a file** when it was not — `--no-upload`, or an upload that could not
 *    reach the bucket.
 *
 * The file picker is offered in BOTH cases and does not go away when the fetch
 * arrives: an operator holding a report from a run against an environment this
 * console cannot reach still has a legitimate reason to open it.
 */
export const Sl1ReportLoader: FC<{
  job: Sl1ImportJobDto | undefined
  source: Sl1ReportSource
}> = ({ job, source }) => {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <Box data-ui="UI-SL2W-175" sx={{ ...panelSx, p: 2.5 }}>
      <Typography sx={eyebrowSx}>{t("sl1ReportSource")}</Typography>

      {/* Said once, plainly, at the point of confusion — and only when it is
          actually true. A run WITH a manifest is fetchable and saying otherwise
          would send the operator hunting for a file they do not need. */}
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", mt: 1, maxWidth: "78ch" }}
      >
        {t(source.localOnly ? "sl1ReportLocalOnly" : "sl1ReportRemoteAvailable")}
      </Typography>

      {job?.reportKey && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 1,
            fontFamily: "monospace",
            color: "text.disabled",
            wordBreak: "break-all",
          }}
        >
          {t("sl1ReportWrittenTo", { key: job.reportKey })}
        </Typography>
      )}

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
        <input
          ref={inputRef}
          type="file"
          accept="application/json,.json"
          style={{ display: "none" }}
          aria-label={t("sl1PickReportFile")}
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) void source.loadFile(f)
            // Reset so picking the SAME file twice re-fires change — otherwise a
            // re-run's report with an unchanged filename silently does nothing.
            e.target.value = ""
          }}
        />
        {/* The primary action when the run has a manifest. */}
        {!source.localOnly && (
          <Button
            size="small"
            variant="contained"
            startIcon={<CloudDownloadIcon />}
            disabled={source.loading}
            onClick={() => void source.loadRemote()}
          >
            {source.loading ? t("sl1Loading") : t("sl1LoadFromStore")}
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={<UploadFileIcon />}
          disabled={source.loading}
          onClick={() => inputRef.current?.click()}
        >
          {source.loading ? t("sl1Loading") : t("sl1PickReportFile")}
        </Button>
        {source.report && (
          <Button size="small" onClick={source.clear}>
            {t("sl1ClearReport")}
          </Button>
        )}
      </Stack>

      {/* Parts loaded, out of parts available. A partially-loaded report must
          never read as a complete one — the counts above it describe the rows
          fetched SO FAR. */}
      {source.report && !source.localOnly && source.findings.partsTotal > 0 && (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {t("sl1FindingsParts", {
              loaded: source.findings.partsLoaded,
              total: source.findings.partsTotal,
              rows: source.findings.rows.length,
            })}
          </Typography>
          {source.findings.partsLoaded < source.findings.partsTotal && (
            <Button
              size="small"
              disabled={source.findings.loading}
              onClick={() => void source.loadMoreFindings()}
            >
              {source.findings.loading
                ? t("sl1Loading")
                : t("sl1LoadNextPart")}
            </Button>
          )}
        </Stack>
      )}

      {source.findings.error && (
        <Alert severity="error" variant="outlined" sx={{ mt: 2 }}>
          {source.findings.error}
        </Alert>
      )}

      {source.error && (
        <Alert severity="error" variant="outlined" sx={{ mt: 2 }}>
          {t(source.error)}
        </Alert>
      )}
    </Box>
  )
}

export default Sl1ReportLoader
